import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {Store} from "@ngrx/store";
import {Router} from "@angular/router";
import {combineLatest, forkJoin, Observable, throwError} from "rxjs";
import {catchError, map, switchMap} from "rxjs/operators";
import {environment} from "../../environments/environment";
import {AuthService} from "./auth.service";

interface DeleteAccountResponse {
  data: null;
  metadata: {
    message: string;
  };
}

interface PasswordValidationResponse {
  data: null;
  metadata: {
    message: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AccountService {

  private apiUrl = environment.api.url;

  user: any;
  token: string;

  constructor(
    private http: HttpClient,
    private store: Store,
    private router: Router,
    private authService: AuthService
  ) { }

  validatePassword(password: string): Observable<{ message: string }> {
    return combineLatest([
      this.authService.authUser(),
      this.authService.token(),
    ]).pipe(
      switchMap(([user, token]) => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`, // Use the token here
          }),
        };

        const url = `${this.apiUrl}/users/validate_password`;

        const requestBody = {userId: user.id, password: password};

        return this.http.post<PasswordValidationResponse>(url, requestBody, httpOptions);
      }),
      map(response => {
        if (!response.metadata || !response.metadata.message) {
          throw new Error('Account deletion failed: No confirmation received');
        }
        return { message: response.metadata.message };
      }),
      catchError(error => {
        console.error('Password validation request failed:', error);
        return throwError(() => new Error('Password validation failed'));
      })
    );
  }

  deleteAccount(): Observable<{ message: string }> {
    // Use combineLatest to wait for both the user and the token
    return combineLatest([
      this.authService.authUser(),
      this.authService.token(),
    ]).pipe(
      switchMap(([user, token]) => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`, // Use the token here
          }),
        };

        if (!user) {
          throw new Error('User is not logged in');
        }

        const url = `${this.apiUrl}/users/${user.id}`;
        return this.http.delete<DeleteAccountResponse>(url, httpOptions);
      }),
      map(response => {
        if (!response.metadata || !response.metadata.message) {
          throw new Error('Account deletion failed: No confirmation received');
        }
        return { message: response.metadata.message };
      }),
      catchError(error => {
        console.error('Account deletion request failed:', error);
        return throwError(() => new Error('Failed to delete account'));
      })
    );
  }

  /* old code
  deleteAccount(): Observable<{ message: string }> {

    this.authService.authUser().subscribe(user => {
      this.user = user;
    });

    this.authService.token().subscribe(token => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}` // Use the token here
      })
    };

    const url = `${this.apiUrl}/users/${this.user.id}`;

    return this.http.delete<DeleteAccountResponse>(url, httpOptions).pipe(
      map(response => {
        // Check that the response fits the expected format
        if (!response.metadata || !response.metadata.message) {
          // If the expected metadata is not present, throw an error
          throw new Error('Account deletion failed: No confirmation received');
        }
        // If response is valid, extract and return the message
        return { message: response.metadata.message };
      }),
      catchError(error => {
        // Handle HTTP errors
        console.error('Account deletion request failed:', error);
        // Optionally transform error before rethrowing
        return throwError(() => new Error('Failed to delete account'));
      })
    );
  }
  */

}
